import notificationSound from "@/assets/sounds/agent-done-notification.mp3";
import { haptic } from "@/utils/haptic";

// Cross-tab coordination using BroadcastChannel
let notificationChannel: BroadcastChannel | null = null;
const pendingNotifications = new Map<string, NodeJS.Timeout>();
const playedNotifications = new Set<string>();
const tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Export for testing
export const resetNotificationState = () => {
    notificationChannel?.close();
    notificationChannel = null;
    pendingNotifications.clear();
    playedNotifications.clear();
};

// Initialize BroadcastChannel for cross-tab coordination
const initNotificationChannel = () => {
    if (notificationChannel) return;

    try {
        if (typeof BroadcastChannel !== "undefined") {
            notificationChannel = new BroadcastChannel("gitpod-agent-notifications");
            notificationChannel.addEventListener("message", handleNotificationMessage);
        }
    } catch (error) {
        console.warn("Failed to initialize BroadcastChannel for notifications:", error);
    }
};

const handleNotificationMessage = (event: MessageEvent) => {
    const data = event.data as { type: string; notificationId: string; tabId: string };
    const { type, notificationId, tabId: senderTabId } = data;

    if (senderTabId === tabId) return; // Ignore our own messages

    if (type === "notification-played") {
        // Another tab played this notification, cancel ours
        const timeout = pendingNotifications.get(notificationId);
        if (timeout) {
            clearTimeout(timeout);
            pendingNotifications.delete(notificationId);
        }
        playedNotifications.add(notificationId);
    }
};

/**
 * Plays a notification sound when an agent execution completes
 * Uses BroadcastChannel to coordinate across tabs and prevent duplicates
 */
export const playAgentDoneNotification = (agentExecutionId?: string) => {
    const notificationId = `agent-done-${agentExecutionId || Date.now()}`;

    // Skip if we already played this notification
    if (playedNotifications.has(notificationId)) {
        return;
    }

    initNotificationChannel();

    // If BroadcastChannel is not supported, play immediately
    if (!notificationChannel) {
        playAgentDoneNotificationInternal();
        return;
    }

    // Set a short timeout to play the sound, allowing other tabs to coordinate
    const timeout = setTimeout(() => {
        if (!playedNotifications.has(notificationId)) {
            playAgentDoneNotificationInternal();
            playedNotifications.add(notificationId);

            // Notify other tabs that we played the sound
            notificationChannel?.postMessage({
                type: "notification-played",
                notificationId,
                tabId,
            });
        }
        pendingNotifications.delete(notificationId);
    }, 50); // 50ms coordination window

    pendingNotifications.set(notificationId, timeout);
};

/**
 * Internal function that actually plays the notification sound
 */
const playAgentDoneNotificationInternal = () => {
    haptic("medium");

    try {
        const audio = new Audio(notificationSound);
        audio.volume = 0.3;

        audio
            .play()
            .then(() => {
                console.log("Notification sound played successfully");
            })
            .catch((error) => {
                console.warn("Failed to play notification sound:", error);
            });
    } catch (error) {
        console.warn("Failed to create Audio element for notification:", error);
    }
};
